import type { PlayerCollisionTelemetry } from '../player'

export interface MacroFrame {
  tick: number
  dt: number
  keys: string[]
  movementX: number
  movementY: number
  mouseDown: boolean
  playerPos?: [number, number, number]
  playerYaw?: number
  telemetry?: PlayerCollisionTelemetry
  events?: Array<{
    name: string
    attributes: Record<string, string | number | boolean>
  }>
}

export interface OtelSpan {
  traceId: string
  spanId: string
  name: string
  startTime: number
  endTime?: number
  attributes: Record<string, string | number | boolean>
  events: Array<{
    name: string
    timestamp: number
    attributes: Record<string, string | number | boolean>
  }>
}

export interface MacroTraceRecording {
  version: '1.0'
  level: string
  timestamp: string
  durationMs: number
  totalTicks: number
  spans: OtelSpan[]
  frames: MacroFrame[]
}

export class MacroRecorder {
  private isRecording = false
  private isPlaying = false
  private frames: MacroFrame[] = []
  private activeKeys = new Set<string>()
  private deltaMouseX = 0
  private deltaMouseY = 0
  private isMouseDown = false
  private tickCount = 0
  private startTime = 0
  private activeSpans: Map<string, OtelSpan> = new Map()
  private completedSpans: OtelSpan[] = []

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', (e) => {
        if (e.key === 'F8') {
          if (this.isRecording) this.stopAndDownload()
          else this.start()
        }
        if (this.isRecording) {
          this.activeKeys.add(e.code)
        }
      })

      window.addEventListener('keyup', (e) => {
        if (this.isRecording) {
          this.activeKeys.delete(e.code)
        }
      })

      window.addEventListener('mousemove', (e) => {
        if (this.isRecording) {
          this.deltaMouseX += e.movementX || 0
          this.deltaMouseY += e.movementY || 0
        }
      })

      window.addEventListener('mousedown', (e) => {
        if (this.isRecording && e.button === 0) {
          this.isMouseDown = true
        }
      })

      window.addEventListener('mouseup', (e) => {
        if (this.isRecording && e.button === 0) {
          this.isMouseDown = false
        }
      })
    }
  }

  start(): void {
    this.isRecording = true
    this.frames = []
    this.completedSpans = []
    this.activeSpans.clear()
    this.tickCount = 0
    this.startTime = performance.now()
    console.log('🔴 [MacroRecorder] Recording started... Press F8 again to stop and download trace.')
    this.startSpan('level.walkthrough', { level: 'tutorial' })
  }

  startSpan(name: string, attributes: Record<string, string | number | boolean> = {}): string {
    const spanId = `span-${Math.random().toString(36).substring(2, 9)}`
    const span: OtelSpan = {
      traceId: `trace-${this.startTime.toFixed(0)}`,
      spanId,
      name,
      startTime: performance.now(),
      attributes,
      events: [],
    }
    this.activeSpans.set(name, span)
    return spanId
  }

  endSpan(name: string, attributes: Record<string, string | number | boolean> = {}): void {
    const span = this.activeSpans.get(name)
    if (span) {
      span.endTime = performance.now()
      Object.assign(span.attributes, attributes)
      this.completedSpans.push(span)
      this.activeSpans.delete(name)
    }
  }

  addEvent(name: string, attributes: Record<string, string | number | boolean> = {}): void {
    const event = {
      name,
      timestamp: performance.now(),
      attributes,
    }
    for (const span of this.activeSpans.values()) {
      span.events.push(event)
    }
  }

  recordTick(
    dt: number,
    playerPos?: { x: number; y: number; z: number },
    playerYaw?: number,
    telemetry?: PlayerCollisionTelemetry,
  ): void {
    if (!this.isRecording) return

    this.frames.push({
      tick: this.tickCount++,
      dt,
      keys: Array.from(this.activeKeys),
      movementX: this.deltaMouseX,
      movementY: this.deltaMouseY,
      mouseDown: this.isMouseDown,
      playerPos: playerPos ? [playerPos.x, playerPos.y, playerPos.z] : undefined,
      playerYaw,
      telemetry,
    })

    // Reset frame deltas
    this.deltaMouseX = 0
    this.deltaMouseY = 0
  }

  stop(): MacroTraceRecording {
    this.isRecording = false
    this.endSpan('level.walkthrough')

    const recording: MacroTraceRecording = {
      version: '1.0',
      level: 'tutorial',
      timestamp: new Date().toISOString(),
      durationMs: performance.now() - this.startTime,
      totalTicks: this.frames.length,
      spans: this.completedSpans,
      frames: this.frames,
    }
    console.log(`⏹ [MacroRecorder] Stopped. Captured ${this.frames.length} frames & ${this.completedSpans.length} OTEL spans.`)
    return recording
  }

  stopAndDownload(): void {
    const data = this.stop()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `esoteria-acceptance-trace-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  get isCurrentlyRecording(): boolean {
    return this.isRecording
  }

  get isCurrentlyPlaying(): boolean {
    return this.isPlaying
  }
}

export class MacroPlayer {
  private readonly recording: MacroTraceRecording
  private currentIndex = 0
  private isFinished = false

  constructor(recording: MacroTraceRecording) {
    this.recording = recording
  }

  step(): MacroFrame | null {
    if (this.currentIndex >= this.recording.frames.length) {
      this.isFinished = true
      return null
    }
    return this.recording.frames[this.currentIndex++]
  }

  get progress(): number {
    return this.recording.frames.length > 0 ? this.currentIndex / this.recording.frames.length : 1
  }

  get currentFrameIndex(): number {
    return this.currentIndex
  }

  get totalFrames(): number {
    return this.recording.frames.length
  }

  get finished(): boolean {
    return this.isFinished
  }

  reset(): void {
    this.currentIndex = 0
    this.isFinished = false
  }
}
